import React from 'react';
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { EVENT_COLORS } from '../../types/calendar-types';
import { formatDateInTimeZone } from '../../utils/timezone';
import { t } from 'i18next';
import { useCalendarViewModel} from "../../view-model/professional/calendar-view-model";
import {observer} from "mobx-react-lite";

// Fonction pour calculer le numéro de la semaine dans l'année


const CalendarHeader: React.FC = observer(() => {
  const calendarViewModel = useCalendarViewModel()

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between p-3 sm:p-5 border-b border-gray-200 dark:border-gray-700 gap-2 sm:gap-4 flex-shrink-0 bg-white dark:bg-gray-900 sticky top-0 z-20">
      <div className="flex items-center justify-between lg:justify-start gap-2 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <Calendar size={16} className="sm:hidden" />
            <Calendar size={20} className="hidden sm:block" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
              {/* Sur mobile, toujours afficher la date du jour */}
              <span className="md:hidden">{formatDateInTimeZone(calendarViewModel.getCurrentDate, 'd MMM')}</span>
              
              {/* Sur desktop, afficher selon la vue */}
              <span className="hidden md:inline">
                {calendarViewModel.getCurrentView === 'day'
                  ? formatDateInTimeZone(calendarViewModel.getCurrentDate, 'EEEE d MMMM')
                  : `${t('calendar.week1', 'Semaine du')} ${formatDateInTimeZone(calendarViewModel.getCurrentDate, 'd MMMM')}`
                }
              </span>
            </h2>
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              <span className="hidden sm:inline">{formatDateInTimeZone(new Date(), 'EEEE d MMMM yyyy')}</span>
              <span className="sm:hidden">{formatDateInTimeZone(new Date(), 'dd/MM/yyyy')}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-4 calendar">
          {/* Contrôles de navigation */}
          <div className="flex items-center gap-2 sm:gap-4 bg-gray-100 dark:bg-gray-800 rounded-lg p-2 sm:p-3 shadow-sm">
            {/* Navigation jour sur mobile */}
            <div 
              onClick={() => {
                const tempView = calendarViewModel.getCurrentView;
                calendarViewModel.setCurrentView('day');
                calendarViewModel.navigateCalendar('prev');
                calendarViewModel.setCurrentView(tempView);
              }}
              className="md:hidden cursor-pointer p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              aria-label="Jour précédent"
            >
              <ChevronLeft size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            
            {/* Navigation normale sur desktop */}
            <div 
              onClick={() => {
                // Navigation vers la semaine précédente
                calendarViewModel.navigateCalendar('prev');
                
                // Récupérer la date actuelle après navigation
                const currentDate = calendarViewModel.getCurrentDate;
                
                // Obtenir dates début/fin de semaine et appeler fetchProfessionalBookings
                const { startDate, endDate } = calendarViewModel.getWeekNumber(currentDate);
                calendarViewModel.fetchProfessionalBookings(startDate, endDate).then();
              }}
              className="hidden md:block cursor-pointer p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              aria-label={calendarViewModel.getCurrentView === 'week' ? "Semaine précédente" : "Jour précédent"}
            >
              <ChevronLeft size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            
            <div
              onClick={() => {
                // Navigation vers aujourd'hui/cette semaine
                calendarViewModel.navigateCalendar('today');
                
                // Récupérer la date actuelle après navigation
                const currentDate = calendarViewModel.getCurrentDate;
                
                // Obtenir dates début/fin de semaine et appeler fetchProfessionalBookings
                const { startDate, endDate } = calendarViewModel.getWeekNumber(currentDate);
                calendarViewModel.fetchProfessionalBookings(startDate, endDate).then();
              }}
              className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded-md transition-colors cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              {/* Sur mobile, toujours afficher "Aujourd'hui" */}
              <span className="md:hidden">
                {t("calendar.day2", "Aujourd'hui")}
              </span>
              
              {/* Sur desktop, afficher selon la vue */}
              <span className="hidden md:inline">
                {calendarViewModel.getCurrentView === 'week' ? t("calendar.week2", "Cette semaine") : t("calendar.day2", "Aujourd'hui")}
              </span>
            </div>
            
            {/* Navigation jour sur mobile */}
            <div 
              onClick={() => {
                const tempView = calendarViewModel.getCurrentView;
                calendarViewModel.setCurrentView('day');
                calendarViewModel.navigateCalendar('next');
                calendarViewModel.setCurrentView(tempView);
              }}
              className="md:hidden cursor-pointer p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              aria-label="Jour suivant"
            >
              <ChevronRight size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            
            {/* Navigation normale sur desktop */}
            <div 
              onClick={() => {
                // Navigation vers la semaine suivante
                calendarViewModel.navigateCalendar('next');
                
                // Récupérer la date actuelle après navigation
                const currentDate = calendarViewModel.getCurrentDate;
                
                // Obtenir dates début/fin de semaine et appeler fetchProfessionalBookings
                const { startDate, endDate } = calendarViewModel.getWeekNumber(currentDate);
                calendarViewModel.fetchProfessionalBookings(startDate, endDate).then();
              }}
              className="hidden md:block cursor-pointer p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              aria-label={calendarViewModel.getCurrentView === 'week' ? "Semaine suivante" : "Jour suivant"}
            >
              <ChevronRight size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          
          {/* Contrôles de changement de vue - masqués sur mobile */}
          <div className="hidden md:flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1.5 shadow-sm">
            {/* Sélecteur de vue */}
            <div className="flex items-center gap-1 ml-auto">
              <button
                onClick={() => calendarViewModel.changeView('day')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${calendarViewModel.getCurrentView === 'day' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                {t('calendar.day', 'Jour')}
              </button>
              <button
                onClick={() => calendarViewModel.changeView('week')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${calendarViewModel.getCurrentView === 'week' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
              >
                {t('calendar.week', 'Semaine')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Légende */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs bg-gray-50 dark:bg-gray-800/50 p-1.5 sm:p-2 rounded-lg">
          {['confirmed', "completed", 'cancelled'].map(status => {
            const color = EVENT_COLORS[status as keyof typeof EVENT_COLORS];
            const statusText = {
              'confirmed': t('calendar.confirmed', 'Confirmé'),
              'completed': t('calendar.completed', 'Terminé'),
              'cancelled': t('calendar.cancelled', 'Annulé'),

            }[status];
            
            return (
              <div key={status} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                <span className="text-gray-600 dark:text-gray-300">{statusText}</span>
              </div>
            );
          })}
        </div>
        
        {/* Bouton d'ajout de rendez-vous */}
        <Button
          onClick={() => calendarViewModel.openNewEventModal()}
          className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white calendar-appointment"
          size="sm"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">{t('calendar.newAppointment')}</span>
        </Button>
      </div>
    </div>
  );
});

export default CalendarHeader;
